import { Icon, Menu, MenuItem, useSnackbar, useTheme } from '@web-chat-core/ui-kit';
import type { Participant, Room } from 'livekit-client';
import { RoomEvent } from 'livekit-client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { findParticipantBySid } from 'useCase/moderation/utils/findParticipantBySid';
import type { ModerationMenuItemModel, ModerationMenuVariant } from './types';
import {
    MenuItemIconWrapper,
    ParticipantMenuIconButton,
    ParticipantMenuWrapper,
    TileMenuIconButton,
    TileMenuIconButtonSmall,
} from './view';

type Props = {
    variant: ModerationMenuVariant;
    items: ModerationMenuItemModel[];
    participant: Participant;
    room: Room | null;
};

const MENU_ESTIMATED_HEIGHT = 280;

export const ModerationMenu = ({ variant, items, participant, room }: Props) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { showSnackbar } = useSnackbar();
    const [isOpen, setIsOpen] = useState(false);
    const [menuAlign, setMenuAlign] = useState<'start' | 'end'>('start');
    const [menuSide, setMenuSide] = useState<'top' | 'bottom'>('bottom');

    const trigger = useMemo(() => {
        if (variant === 'tile') {
            return (
                <TileMenuIconButton className="moderation-menu-button">
                    <Icon icon="icDots" colorVariant="basic" size={24} />
                </TileMenuIconButton>
            );
        }
        if (variant === 'tile-small') {
            return (
                <TileMenuIconButtonSmall className="moderation-menu-button" data-variant="tile-small">
                    <Icon icon="icDots16" colorVariant="basic" size={16} />
                </TileMenuIconButtonSmall>
            );
        }
        return (
            <ParticipantMenuIconButton className="moderation-menu-button">
                <Icon icon="icDots16" colorVariant="basic" size={16} />
            </ParticipantMenuIconButton>
        );
    }, [variant]);

    const handleOpenChange = useCallback(
        (open: boolean, triggerRef?: HTMLElement) => {
            // из-за того что collision detection в radix работает вдоль одной оси
            // приходится обрабатывать кейс когда меню открывается слева от триггера
            // но по высоте не влазит на нижних карточках(спикер мод)
            // поэтому вручную проверяем позицию и если не влазит то меняем направление на top
            if (open && triggerRef) {
                const rect = triggerRef.getBoundingClientRect();
                const spaceBelow = window.innerHeight - rect.bottom;
                const isTileVariant = variant === 'tile-small' || variant === 'tile';

                if (isTileVariant) {
                    setMenuAlign(spaceBelow < MENU_ESTIMATED_HEIGHT ? 'end' : 'start');
                } else {
                    setMenuSide(spaceBelow < MENU_ESTIMATED_HEIGHT ? 'top' : 'bottom');
                }
            }
            if (!open) {
                setMenuAlign('start');
                setMenuSide('bottom');
            }
            setIsOpen(open);
        },
        [variant],
    );

    // проверяем что участник еще в комнате когда меню открыто
    // если нет то показываем снекбар
    useEffect(() => {
        if (!isOpen || !room || !participant?.sid) return;

        const checkParticipant = () => {
            const foundParticipant = findParticipantBySid(room, participant.sid);
            if (!foundParticipant) {
                const errorMessage = t('web_videoconference_action_not_completed_user_left');
                showSnackbar(errorMessage);
                setIsOpen(false);
            }
        };

        // проверяем сразу при открытии меню
        checkParticipant();

        // подписываемся на событие отключения участника
        const onParticipantDisconnected = (disconnectedParticipant: Participant) => {
            if (disconnectedParticipant.sid === participant.sid) {
                // это тот участник на котором открыто меню
                const errorMessage = t('web_videoconference_action_not_completed_user_left');
                showSnackbar(errorMessage);
                setIsOpen(false);
            }
        };

        room.on(RoomEvent.ParticipantDisconnected, onParticipantDisconnected);

        return () => {
            room.off(RoomEvent.ParticipantDisconnected, onParticipantDisconnected);
        };
    }, [isOpen, room, participant?.sid, t, showSnackbar]);

    const menuContentProps = useMemo(() => {
        const isTileVariant = variant === 'tile-small' || variant === 'tile';

        return {
            side: isTileVariant ? ('left' as const) : menuSide,
            sideOffset: 5,
            align: isTileVariant ? menuAlign : ('end' as const),
            alignOffset: isTileVariant ? 0 : 10,
            style: {
                background: theme.root.colors.background.sheetOrModal,
            },
            onInteractOutside: () => {
                handleOpenChange(false);
            },
        };
    }, [theme, handleOpenChange, variant, menuAlign, menuSide]);

    const menu = (
        <Menu
            toggleButtonContent={trigger}
            contentProps={menuContentProps}
            isForcedOpen={isOpen}
            onToggleMenu={handleOpenChange}
            modal={false}
        >
            {items.map((item) => (
                <MenuItem key={item.id} id={item.id} onClick={item.onClick}>
                    {item.icon && <MenuItemIconWrapper>{item.icon}</MenuItemIconWrapper>}
                    {item.label}
                </MenuItem>
            ))}
        </Menu>
    );

    return variant === 'list' ? <ParticipantMenuWrapper>{menu}</ParticipantMenuWrapper> : menu;
};
